import React, { useState, useMemo, useEffect } from 'react';
import { Play, Pause, RotateCcw, Info, AlertTriangle, CheckCircle2, ArrowRightLeft, Gauge } from 'lucide-react';

// ══════════════════════════════════════════════════════════════════════════
// PILOTE QUALITÉ · O7 · Voies d'accélération / décélération (hors agglo)
// Œuvre pédagogique interactive :
//  · Vue de dessus : autoroute 2 voies + voie d'accélération OU de décélération
//  · Toggle "Insertion" (accélération) / "Sortie" (décélération)
//  · Jauge de vitesse animée (30→110 km/h en insertion, 110→50 en sortie)
//  · 3 véhicules autoroute en boucle SMIL, vitesse variable
//  · Voiture EGO qui progresse le long de la voie avec accélération réaliste
//  · Panneau : les 3 étapes, différences vs O6, sanctions, chiffres clés
// ══════════════════════════════════════════════════════════════════════════

export default function VoiesAccelDecelSvg() {
  const [mode, setMode] = useState('acceleration'); // 'acceleration' | 'deceleration'
  const [playing, setPlaying] = useState(false);
  const [phase, setPhase] = useState(0); // 0=préparation, 1=action, 2=fusion/sortie, 3=OK
  const [egoSpeed, setEgoSpeed] = useState(mode === 'acceleration' ? 40 : 110);

  // Reset speed on mode change
  useEffect(() => {
    setPhase(0);
    setEgoSpeed(mode === 'acceleration' ? 40 : 110);
    setPlaying(false);
  }, [mode]);

  // Autoplay séquence
  useEffect(() => {
    if (!playing) return undefined;
    const interval = setInterval(() => {
      setPhase((p) => {
        if (p >= 3) {
          setPlaying(false);
          return p;
        }
        const newPhase = p + 1;
        // Vitesse cible synchronisée sur la nouvelle phase
        if (mode === 'acceleration') {
          const targets = [40, 60, 90, 110];
          setEgoSpeed(targets[newPhase]);
        } else {
          const targets = [110, 90, 70, 50];
          setEgoSpeed(targets[newPhase]);
        }
        return newPhase;
      });
    }, 1500);
    return () => clearInterval(interval);
  }, [playing, mode]);

  const reset = () => {
    setPhase(0);
    setEgoSpeed(mode === 'acceleration' ? 40 : 110);
    setPlaying(false);
  };

  const isAccel = mode === 'acceleration';

  // Position EGO selon phase et mode
  const egoPos = useMemo(() => {
    if (isAccel) {
      // acc : commence bas droite, remonte diagonale, fusionne
      switch (phase) {
        case 0: return { x: 130, y: 480, angle: -8 };
        case 1: return { x: 350, y: 450, angle: -10 };
        case 2: return { x: 560, y: 380, angle: -20 };
        case 3: return { x: 720, y: 300, angle: 0 };
        default: return { x: 130, y: 480, angle: -8 };
      }
    }
    // dec : commence en haut voie principale, descend vers voie sortie
    switch (phase) {
      case 0: return { x: 100, y: 300, angle: 0 };
      case 1: return { x: 300, y: 320, angle: 8 };
      case 2: return { x: 490, y: 400, angle: 15 };
      case 3: return { x: 680, y: 480, angle: 5 };
      default: return { x: 100, y: 300, angle: 0 };
    }
  }, [phase, isAccel]);

  const phaseLabels = isAccel
    ? [
        { title: 'Phase 1 · Cap voie 3', desc: "Clignotant gauche activé. Regard dans le rétro extérieur gauche.", color: '#3b82f6' },
        { title: 'Phase 2 · Accélération franche', desc: "J'accélère franchement. But : atteindre la vitesse du flux avant la ligne de fusion.", color: '#f59e0b' },
        { title: 'Phase 3 · Fusion', desc: "Je m'insère dans le gap repéré, vitesse alignée. Angle doux.", color: '#10b981' },
        { title: 'Phase 4 · Sur autoroute', desc: "Clignotant coupé. Contrôle rétro pour le suiveur.", color: '#22c55e' },
      ]
    : [
        { title: 'Phase 1 · Signal', desc: "Clignotant droit AVANT la voie de décélération. Rétros contrôlés.", color: '#3b82f6' },
        { title: 'Phase 2 · Bascule', desc: "Je bascule sur la voie de décélération SANS freiner encore.", color: '#f59e0b' },
        { title: 'Phase 3 · Freinage', desc: "Je décélère franchement sur la voie dédiée pour ne pas ralentir le flux.", color: '#eab308' },
        { title: 'Phase 4 · Fin de sortie', desc: "Allure adaptée à la bretelle. Contrôle rétro intérieur.", color: '#22c55e' },
      ];

  const currentP = phaseLabels[phase];

  return (
    <div className="w-full">
      {/* Barre d'outils */}
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <div className="inline-flex rounded-full border border-white/15 overflow-hidden">
          <button
            type="button"
            onClick={() => setMode('acceleration')}
            data-testid="o7-mode-accel"
            className={`h-9 px-3.5 text-[11.5px] font-bold font-mono-spec inline-flex items-center gap-1.5 transition ${
              isAccel ? 'bg-emerald-500 text-emerald-950' : 'text-white/60 hover:bg-white/5'
            }`}
          >
            <ArrowRightLeft size={13} /> Insertion (accélération)
          </button>
          <button
            type="button"
            onClick={() => setMode('deceleration')}
            data-testid="o7-mode-decel"
            className={`h-9 px-3.5 text-[11.5px] font-bold font-mono-spec inline-flex items-center gap-1.5 transition border-l border-white/15 ${
              !isAccel ? 'bg-orange-500 text-orange-950' : 'text-white/60 hover:bg-white/5'
            }`}
          >
            <ArrowRightLeft size={13} className="rotate-180" /> Sortie (décélération)
          </button>
        </div>
        {!playing ? (
          <button
            type="button"
            onClick={() => { if (phase >= 3) reset(); setPlaying(true); }}
            data-testid="o7-play"
            className="inline-flex items-center gap-1.5 h-9 px-3 rounded-full border border-cyan-500/40 text-cyan-200 hover:bg-cyan-500/10 text-[12px] font-semibold"
          >
            <Play size={13} /> Jouer
          </button>
        ) : (
          <button
            type="button"
            onClick={() => setPlaying(false)}
            data-testid="o7-pause"
            className="inline-flex items-center gap-1.5 h-9 px-3 rounded-full bg-amber-500 text-amber-950 font-bold text-[12px]"
          >
            <Pause size={13} /> Pause
          </button>
        )}
        <button
          type="button"
          onClick={reset}
          data-testid="o7-reset"
          className="inline-flex items-center gap-1.5 h-9 px-3 rounded-full border border-white/20 text-white/70 hover:bg-white/5 text-[12px] font-semibold"
        >
          <RotateCcw size={13} /> Reset
        </button>
        <div className="ml-auto flex items-center gap-2 text-[11px] text-white/60 font-mono-spec">
          <Gauge size={13} />
          <span>EGO : {egoSpeed} km/h</span>
        </div>
      </div>

      <div className="grid lg:grid-cols-[1fr,340px] gap-4">
        {/* SVG autoroute */}
        <div className="rounded-2xl overflow-hidden border border-white/10 bg-gradient-to-b from-[#0f1a2b] to-[#0a1220]">
          <svg viewBox="0 0 800 700" className="w-full h-auto block max-h-[48vh] sm:max-h-[52vh]" role="img" aria-label="Voies d'accélération et de décélération">
            <defs>
              <linearGradient id="o7-grass" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#065f46" />
                <stop offset="100%" stopColor="#064e3b" />
              </linearGradient>
              <linearGradient id="o7-road" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#334155" />
                <stop offset="100%" stopColor="#1e293b" />
              </linearGradient>
              <linearGradient id="o7-ego" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#f5f5f5" />
                <stop offset="100%" stopColor="#94a3b8" />
              </linearGradient>
            </defs>

            {/* Herbe hors autoroute */}
            <rect x={0} y={0} width={800} height={700} fill="url(#o7-grass)" />
            {/* Petites variations herbe */}
            {[[80, 60], [200, 80], [560, 70], [720, 60], [100, 560], [280, 610], [640, 590]].map(([x, y], i) => (
              <circle key={i} cx={x} cy={y} r={8} fill="#047857" opacity={0.4} />
            ))}

            {/* AUTOROUTE — 2 voies principales */}
            <rect x={0} y={220} width={800} height={140} fill="url(#o7-road)" />
            {/* Terre-plein central (bande blanche pleine) */}
            <line x1={0} y1={220} x2={800} y2={220} stroke="#fff" strokeWidth={3} opacity={0.9} />
            <rect x={0} y={215} width={800} height={5} fill="#dc2626" opacity={0.5} />
            {/* Séparation des 2 voies (pointillé) */}
            <line x1={0} y1={290} x2={800} y2={290} stroke="#fff" strokeWidth={2.5} strokeDasharray="30 18" opacity={0.75} />
            {/* Bord droit autoroute (bande d'urgence) */}
            <line x1={0} y1={360} x2={800} y2={360} stroke="#fff" strokeWidth={3} opacity={0.85} />
            <rect x={0} y={355} width={800} height={5} fill="#f59e0b" opacity={0.4} />

            {/* Voie d'accélération (bas droite, trapèze) OU décélération selon mode */}
            {isAccel ? (
              <>
                <polygon points="80,510 800,510 800,360 620,360" fill="url(#o7-road)" stroke="#fff" strokeWidth={2} strokeOpacity={0.5} />
                {/* Ligne fusion (pointillé long) */}
                <line x1={620} y1={360} x2={80} y2={510} stroke="#fff" strokeWidth={2.5} strokeDasharray="30 12" opacity={0.7} />
                {/* Flèche verte accélération */}
                <path d="M 200 490 L 580 380 M 580 380 L 555 385 M 580 380 L 570 400" stroke="#22c55e" strokeWidth={3} fill="none" opacity={0.55} />
                <text x={280} y={475} fill="#22c55e" fontSize={11} fontWeight="bold" fontFamily="monospace" opacity={0.9}>ACCÉLÈRE ICI</text>
                {/* Panneau bretelle d'accès */}
                <g transform="translate(120 460)">
                  <rect x={-30} y={-16} width={60} height={32} rx={4} fill="#0891b2" stroke="#fff" strokeWidth={2} />
                  <text x={0} y={-2} textAnchor="middle" fill="#fff" fontSize={8} fontWeight="bold">A-XX</text>
                  <text x={0} y={9} textAnchor="middle" fill="#fff" fontSize={7}>PARIS</text>
                </g>
              </>
            ) : (
              <>
                <polygon points="80,360 620,360 800,510 800,510" fill="url(#o7-road)" stroke="#fff" strokeWidth={2} strokeOpacity={0.5} />
                <line x1={80} y1={360} x2={620} y2={360} stroke="#fff" strokeWidth={2.5} strokeDasharray="30 12" opacity={0.4} />
                <line x1={620} y1={360} x2={800} y2={510} stroke="#fff" strokeWidth={2.5} opacity={0.75} />
                {/* Flèche orange décélération */}
                <path d="M 200 385 L 580 460 M 580 460 L 555 460 M 580 460 L 568 445" stroke="#f97316" strokeWidth={3} fill="none" opacity={0.55} />
                <text x={280} y={430} fill="#f97316" fontSize={11} fontWeight="bold" fontFamily="monospace" opacity={0.9}>DÉCÉLÈRE ICI</text>
                {/* Panneau sortie */}
                <g transform="translate(700 470)">
                  <rect x={-40} y={-18} width={80} height={36} rx={4} fill="#166534" stroke="#fff" strokeWidth={2} />
                  <text x={0} y={-4} textAnchor="middle" fill="#fff" fontSize={9} fontWeight="bold">SORTIE</text>
                  <text x={0} y={9} textAnchor="middle" fill="#fff" fontSize={11}>▶</text>
                </g>
              </>
            )}

            {/* Panneau distance sortie/entrée */}
            {!isAccel && (
              <g transform="translate(60 190)">
                <rect x={-40} y={-14} width={80} height={28} rx={2} fill="#166534" stroke="#fff" strokeWidth={1.5} />
                <text x={0} y={5} textAnchor="middle" fill="#fff" fontSize={11} fontWeight="bold">1000 m ▶</text>
              </g>
            )}

            {/* VÉHICULES AUTOROUTE (2 voies) */}
            {[
              { y: 250, dur: 5, color: '#3b82f6', delay: 0 },
              { y: 250, dur: 5, color: '#8b5cf6', delay: 1.5 },
              { y: 320, dur: 6, color: '#f97316', delay: 0.7 },
              { y: 320, dur: 6, color: '#06b6d4', delay: 3.2 },
            ].map((v, i) => (
              <g key={i}>
                <g transform="translate(0 0)">
                  <rect x={-28} y={-16} width={56} height={32} rx={9} fill={v.color} stroke="#0b1120" strokeWidth={2} />
                  <rect x={-22} y={-10} width={44} height={13} rx={2} fill="#0b1120" opacity={0.55} />
                  <rect x={-26} y={-16} width={4} height={4} fill="#fbbf24" />
                  <rect x={22} y={-16} width={4} height={4} fill="#fbbf24" />
                  <rect x={-26} y={12} width={4} height={4} fill="#dc2626" />
                  <rect x={22} y={12} width={4} height={4} fill="#dc2626" />
                </g>
                <animateTransform
                  attributeName="transform"
                  type="translate"
                  from={`-80 ${v.y}`}
                  to={`900 ${v.y}`}
                  dur={`${v.dur}s`}
                  begin={`${-v.delay}s`}
                  repeatCount="indefinite"
                />
              </g>
            ))}

            {/* GAP repéré (uniquement en insertion phase >= 1) */}
            {isAccel && phase >= 1 && (
              <g>
                <circle cx={620} cy={320} r={30} fill="none" stroke="#22c55e" strokeWidth={3} strokeDasharray="6 4">
                  <animate attributeName="stroke-opacity" values="1;0.3;1" dur="1s" repeatCount="indefinite" />
                  <animate attributeName="r" values="28;36;28" dur="1.5s" repeatCount="indefinite" />
                </circle>
                <text x={620} y={285} textAnchor="middle" fill="#22c55e" fontSize={10} fontWeight="bold">TROU</text>
              </g>
            )}

            {/* VOITURE EGO — position et rotation animées */}
            <g style={{ transition: 'transform 1.4s cubic-bezier(0.34, 1.56, 0.64, 1)' }} transform={`translate(${egoPos.x} ${egoPos.y}) rotate(${egoPos.angle})`}>
              <rect x={-22} y={-15} width={44} height={30} rx={9} fill="url(#o7-ego)" stroke="#0b1120" strokeWidth={2.5} />
              <rect x={-17} y={-10} width={34} height={12} rx={2} fill="#0ea5e9" opacity={0.5} />
              {/* Clignotant selon mode */}
              {phase < 3 && (
                isAccel ? (
                  <rect x={-22} y={-15} width={5} height={4} fill="#fbbf24">
                    <animate attributeName="opacity" values="1;0.1;1" dur="0.5s" repeatCount="indefinite" />
                  </rect>
                ) : (
                  <rect x={17} y={-15} width={5} height={4} fill="#fbbf24">
                    <animate attributeName="opacity" values="1;0.1;1" dur="0.5s" repeatCount="indefinite" />
                  </rect>
                )
              )}
              <text x={0} y={4} textAnchor="middle" fill="#0b1120" fontSize={10} fontWeight="900">EGO</text>
            </g>

            {/* JAUGE VITESSE EGO (grande) */}
            <g transform="translate(30 570)">
              <text x={0} y={0} fill="#94a3b8" fontSize={10} fontWeight="bold" fontFamily="monospace">VITESSE EGO (km/h)</text>
              <rect x={0} y={10} width={280} height={20} rx={10} fill="#0b1120" stroke="#334155" strokeWidth={1.5} />
              <rect x={3} y={13} width={(egoSpeed / 130) * 274} height={14} rx={7} fill={isAccel ? '#22c55e' : '#f97316'}
                    style={{ transition: 'width 1.2s ease-in-out' }} />
              {/* Marqueur 90 km/h autoroute */}
              <line x1={3 + (90 / 130) * 274} y1={8} x2={3 + (90 / 130) * 274} y2={32} stroke="#fbbf24" strokeWidth={1.5} strokeDasharray="2 2" />
              <text x={3 + (90 / 130) * 274} y={44} textAnchor="middle" fill="#fbbf24" fontSize={9} fontFamily="monospace">90</text>
              <text x={295} y={23} fill="#fff" fontSize={14} fontWeight="900" fontFamily="monospace">{egoSpeed}</text>
            </g>

            {/* Étapes de la séquence */}
            <g transform="translate(410 570)">
              <text x={0} y={0} fill="#94a3b8" fontSize={10} fontWeight="bold" fontFamily="monospace">SÉQUENCE</text>
              {phaseLabels.map((p, i) => (
                <g key={i} transform={`translate(${i * 88} 10)`}>
                  <circle cx={12} cy={12} r={12} fill={i <= phase ? p.color : '#334155'} />
                  <text x={12} y={16} textAnchor="middle" fill="#fff" fontSize={11} fontWeight="900">{i + 1}</text>
                  {i < 3 && (
                    <line x1={26} y1={12} x2={62} y2={12} stroke={i < phase ? p.color : '#334155'} strokeWidth={2} />
                  )}
                </g>
              ))}
            </g>

            {/* Bandeau bas */}
            <g transform="translate(0, 655)">
              <rect width={800} height={45} fill="#000" opacity={0.6} />
              <text x={20} y={28} fill="#fff" fontSize={13} fontWeight="bold">
                O7 · {isAccel ? "Voie d'accélération" : "Voie de décélération"} (hors agglo)
              </text>
              <text x={20} y={42} fill="#94a3b8" fontSize={11}>REMC C3e — Entrée / sortie d&apos;autoroute</text>
              <circle cx={770} cy={22} r={16} fill="#dc2626" />
              <text x={770} y={27} textAnchor="middle" fill="#fff" fontSize={11} fontWeight="900">CV</text>
            </g>
          </svg>
        </div>

        {/* Panneau latéral */}
        <div className="rounded-2xl border border-white/10 bg-[#0d0d0e] p-4 sm:p-5 self-start lg:max-h-[calc(100vh-300px)] lg:overflow-y-auto lg:custom-scroll" data-testid="o7-panel">
          <div className="rounded-xl border-2 p-3.5" style={{ borderColor: currentP.color, background: currentP.color + '18' }}>
            <div className="text-[10.5px] font-mono-spec uppercase tracking-wider font-bold" style={{ color: currentP.color }}>
              {currentP.title}
            </div>
            <p className="mt-1.5 text-white/90 text-[12.5px] leading-relaxed">{currentP.desc}</p>
          </div>

          <div className="mt-4 grid grid-cols-2 gap-2">
            <div className="rounded-lg border border-white/10 bg-white/[0.02] p-2.5 text-center">
              <div className="text-white/50 text-[9.5px] font-mono-spec uppercase tracking-wider font-bold">Longueur voie</div>
              <div className="mt-0.5 font-serif-editorial text-white text-[16px]">{isAccel ? '200-400 m' : '250-500 m'}</div>
            </div>
            <div className="rounded-lg border border-white/10 bg-white/[0.02] p-2.5 text-center">
              <div className="text-white/50 text-[9.5px] font-mono-spec uppercase tracking-wider font-bold">Objectif vitesse</div>
              <div className="mt-0.5 font-serif-editorial text-white text-[16px]">{isAccel ? '110-130' : '50-90'} <span className="text-white/50 text-[11px]">km/h</span></div>
            </div>
          </div>

          <div className="mt-3 rounded-lg border border-cyan-500/30 bg-cyan-950/20 p-3 flex items-start gap-2">
            <Info size={14} className="text-cyan-300 mt-0.5 shrink-0" />
            <div>
              <div className="text-cyan-300 text-[10.5px] font-mono-spec uppercase tracking-wider font-bold">
                {isAccel ? 'Priorité au flux' : 'Ne pas ralentir le flux'}
              </div>
              <p className="mt-1 text-white/85 text-[12px] leading-relaxed">
                {isAccel
                  ? "L'autoroute a la priorité — c'est TOI qui t'insères. Adapte ta vitesse, ne force jamais le passage."
                  : "Reste à ta vitesse d'autoroute JUSQU'À la voie de décélération. Le freinage se fait DEDANS, jamais avant."}
              </p>
            </div>
          </div>

          <div className="mt-3 rounded-lg border border-emerald-500/40 bg-emerald-950/20 p-3 flex items-start gap-2">
            <CheckCircle2 size={14} className="text-emerald-300 mt-0.5 shrink-0" />
            <div>
              <div className="text-emerald-300 text-[10.5px] font-mono-spec uppercase tracking-wider font-bold">✓ Différence avec O6</div>
              <p className="mt-1 text-white/85 text-[12px] leading-relaxed">
                Autoroute = voie DÉDIÉE d&apos;accélération/décélération, plus longue et à + haute vitesse. En centre-ville (O6), tu t&apos;insères directement.
              </p>
            </div>
          </div>

          <div className="mt-3 rounded-lg border border-amber-500/40 bg-amber-950/20 p-3 flex items-start gap-2">
            <AlertTriangle size={14} className="text-amber-300 mt-0.5 shrink-0" />
            <div>
              <div className="text-amber-300 text-[10.5px] font-mono-spec uppercase tracking-wider font-bold">⚠ Piège fréquent</div>
              <p className="mt-1 text-white/85 text-[12px] leading-relaxed">
                {isAccel
                  ? "S'arrêter en bout de bretelle d'accès (STOP en pleine voie d'accélération) = collision arrière quasi-garantie. Sur autoroute, on ne S'ARRÊTE JAMAIS pour s'insérer."
                  : "Freiner sur l'autoroute AVANT la voie de sortie = accident. Freiner TOUJOURS dans la voie de décélération."}
              </p>
            </div>
          </div>

          <div className="mt-3 rounded-md border border-red-500/40 bg-red-950/20 p-2.5 text-[11.5px] text-red-100 leading-relaxed">
            <span className="text-red-300 font-mono-spec text-[10px] uppercase tracking-wider font-bold block mb-0.5">Sanctions</span>
            Refus de priorité : 135 € · 4 pts · Circulation dangereuse sur autoroute : 90 à 375 € · 3 pts.
          </div>
        </div>
      </div>
    </div>
  );
}
